/**
 * Single orchestration path for analytics consent revoke (P3.1-01).
 *
 * Order: PATCH sessions → revoke-by-key → clear local storage.
 */
import { clearAnonymousKey } from './analyticsAnonymousStorage.js';
import { clearStoredAnalyticsSessionIds } from './analyticsSessionStorage.js';
import { logAnalyticsDevError } from 'pi-kiosk-shared/analyticsDevLog';

export interface AnalyticsConsentRevokeClient {
  patchSessionConsentRevoked(sessionId: string): Promise<void>;
  revokeConsentByKey(anonymousKey: string): Promise<void>;
}

export interface PerformAnalyticsConsentRevokeArgs {
  readonly tenantCode: string;
  readonly sessionIds: readonly string[];
  /** Caller must read once before any storage clear. */
  readonly anonymousKey: string | null;
  readonly client: AnalyticsConsentRevokeClient;
  readonly storage?: Parameters<typeof clearAnonymousKey>[1];
  readonly sessionStorage?: Parameters<typeof clearStoredAnalyticsSessionIds>[1];
  /** When false, local key/session MRU are kept (e.g. PUT preferences failed). */
  readonly clearLocalAfterSuccess?: boolean;
}

export async function performAnalyticsConsentRevoke(
  args: PerformAnalyticsConsentRevokeArgs,
): Promise<void> {
  const { tenantCode, sessionIds, anonymousKey, client } = args;
  const localStorage = args.storage;
  const sessionStorage = args.sessionStorage;
  const shouldClearLocal = args.clearLocalAfterSuccess !== false;

  if (sessionIds.length > 0) {
    let patchFailures = 0;
    await Promise.all(
      sessionIds.map((sessionId) =>
        client.patchSessionConsentRevoked(sessionId).catch((err: unknown) => {
          patchFailures += 1;
          logAnalyticsDevError('consent_revoke_patch_failed', {
            sessionId,
            reason: err instanceof Error ? err.message : String(err),
          });
        }),
      ),
    );
    if (patchFailures > 0) {
      return;
    }
  } else if (anonymousKey !== null) {
    try {
      await client.revokeConsentByKey(anonymousKey);
    } catch (err: unknown) {
      logAnalyticsDevError('consent_revoke_by_key_failed', {
        reason: err instanceof Error ? err.message : String(err),
      });
      return;
    }
  }

  if (!shouldClearLocal) {
    return;
  }

  clearAnonymousKey(tenantCode, localStorage ?? undefined);
  clearStoredAnalyticsSessionIds(tenantCode, sessionStorage ?? undefined);
}
